import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';

const EMPTY_FILTERS = {
  dataBalancoInicio: '',
  dataBalancoFim: '',
  conta: '',
  descricao: '',
  valorMin: '',
  valorMax: ''
};

const ReceitaFilterBar = ({ filters, onApply, onClear }) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const isInitialMount = useRef(true);

  // Sincronizar quando o pai limpa os filtros externamente
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setLocalFilters(filters);
  }, [filters]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setLocalFilters(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleApply = useCallback(() => {
    onApply(localFilters);
  }, [localFilters, onApply]);

  const handleClear = useCallback(() => {
    setLocalFilters(EMPTY_FILTERS);
    onClear();
  }, [onClear]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') handleApply();
  }, [handleApply]);

  const hasActiveFilters = Object.values(localFilters).some(v => v !== '');

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faSearch} className="mr-2 text-blue-500" />
          Filtros de Pesquisa
        </h3>
        {hasActiveFilters && (
          <button
            onClick={handleClear}
            className="flex items-center px-3 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="mr-1" />
            Limpar Filtros
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <div>
          <label htmlFor="dataBalancoInicio" className="block text-sm font-medium text-gray-700 mb-1">
            Data Balanço (De)
          </label>
          <input
            type="date"
            id="dataBalancoInicio"
            name="dataBalancoInicio"
            value={localFilters.dataBalancoInicio || ''}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="dataBalancoFim" className="block text-sm font-medium text-gray-700 mb-1">
            Data Balanço (Até)
          </label>
          <input
            type="date"
            id="dataBalancoFim"
            name="dataBalancoFim"
            value={localFilters.dataBalancoFim || ''}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="conta" className="block text-sm font-medium text-gray-700 mb-1">
            Conta
          </label>
          <input
            type="text"
            id="conta"
            name="conta"
            value={localFilters.conta || ''}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Filtrar por conta"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="descricao" className="block text-sm font-medium text-gray-700 mb-1">
            Descrição
          </label>
          <input
            type="text"
            id="descricao"
            name="descricao"
            value={localFilters.descricao || ''}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Filtrar por descrição"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="valorMin" className="block text-sm font-medium text-gray-700 mb-1">
            Valor Mínimo
          </label>
          <input
            type="number"
            id="valorMin"
            name="valorMin"
            value={localFilters.valorMin || ''}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Valor mínimo"
            step="0.01"
            min="0"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="valorMax" className="block text-sm font-medium text-gray-700 mb-1">
            Valor Máximo
          </label>
          <input
            type="number"
            id="valorMax"
            name="valorMax"
            value={localFilters.valorMax || ''}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Valor máximo"
            step="0.01"
            min="0"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <button
          onClick={handleApply}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-md shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          <FontAwesomeIcon icon={faSearch} />
          Filtrar
        </button>
      </div>
    </div>
  );
};

export default React.memo(ReceitaFilterBar);